'use client';

import * as React from 'react';
import { createSelectorMemoized, useStore } from '@mui/x-internals/store';
import { chatSelectors } from "../selectors/index.js";
import { useChatStore } from "./useChatStore.js";

/**
 * Controls when the streaming indicator renders.
 * - `'auto'` – shown only in assistant-backed conversations (auto-detected).
 * - `true` – always shown while a response is in flight.
 * - `false` – never shown.
 */

// Narrow, boolean-valued selectors: streamed chunks replace the streaming
// message object on every flush, so subscribing to the `messages` array would
// re-render the indicator per chunk. These recompute per chunk but return
// stable booleans, so the subscription only re-renders when a value flips.
const selectHasAssistantMessage = createSelectorMemoized(state => state.messageIds, state => state.messagesById, (messageIds, messagesById) => messageIds.some(id => messagesById[id]?.role === 'assistant'));
const selectLastMessageIsStreamingAssistant = createSelectorMemoized(state => state.messageIds, state => state.messagesById, (messageIds, messagesById) => {
  const lastMessage = messagesById[messageIds[messageIds.length - 1] ?? ''];
  return lastMessage?.role === 'assistant' && lastMessage.status === 'streaming';
});

/**
 * Visibility gating shared by the built-in streaming indicator and custom
 * `streamingIndicator` slot components.
 */
export function useStreamingIndicatorVisibility(mode = 'auto') {
  const store = useChatStore();
  const isStreaming = useStore(store, chatSelectors.isStreaming);
  const streamingConversationId = useStore(store, chatSelectors.streamingConversationId);
  const activeConversationId = useStore(store, chatSelectors.activeConversationId);
  const hasAssistantMessage = useStore(store, selectHasAssistantMessage);
  const hasStreamingAssistantMessage = useStore(store, selectLastMessageIsStreamingAssistant);

  // `store.assistantUser` resolves an assistant member from members → active
  // conversation participants → message authors. The `hasAssistantMessage`
  // subscription covers author-less assistant messages.
  const isAgentLike = store.assistantUser != null || hasAssistantMessage;
  const waiting = mode !== false && isStreaming && (streamingConversationId == null || streamingConversationId === activeConversationId) && !hasStreamingAssistantMessage && (mode === true || isAgentLike);
  return React.useMemo(() => ({
    waiting
  }), [waiting]);
}